import { jsPDF } from 'jspdf';
import type { StructuredData } from '../api/types';
import type { ContractorSettings } from '../lib/contractorSettings';
import type { PdfLabels } from './pdfHelpers';
import {
  buildFooterRows,
  getTableFinalY,
  renderContractorDetails,
  renderItemsTable,
  renderLogo,
  renderNotesSection,
  renderPageFooter,
  resolveLabels,
} from './pdfHelpers';

export interface GenerateInvoicePdfInput {
  structured_data: StructuredData;
  created_at: string;
  contractor: ContractorSettings;
  vatRate: number | null;
  markupPercent: number | null;
  quoteId?: string;
  invoiceNumber: string;
  locale?: string;
  labels?: Partial<PdfLabels>;
  logo?: string | null;
  /** @deprecated Use labels.notesHeading instead */
  notesHeading?: string;
  /** @deprecated Use labels.invoiceTitle instead */
  invoiceTitle?: string;
  /** @deprecated Use labels.invoiceNumberLabel instead */
  invoiceNumberLabel?: string;
  /** @deprecated Use labels.invoiceDateLabel instead */
  invoiceDateLabel?: string;
  /** @deprecated Use labels.dueDateLabel instead */
  dueDateLabel?: string;
  /** @deprecated Use labels.quoteRefLabel instead */
  quoteRefLabel?: string;
  /** @deprecated Use labels.paymentInfoHeading instead */
  paymentInfoHeading?: string;
}

interface InvoiceMetadataConfig {
  structured_data: StructuredData;
  contractor: ContractorSettings;
  invoiceNumber: string;
  locale?: string;
  quoteId?: string;
  labels: PdfLabels;
}

function renderInvoiceMetadata(doc: jsPDF, config: InvoiceMetadataConfig, startY: number): number {
  const { structured_data, contractor, invoiceNumber, locale, quoteId, labels } = config;

  const dateLocale = locale || 'en-US';
  const today = new Date();
  const formattedDate = today.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const dueDate = new Date(today);
  dueDate.setDate(dueDate.getDate() + contractor.paymentTermDays);
  const formattedDueDate = dueDate.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const pageWidth = doc.internal.pageSize.width;
  let yPos = startY;

  doc.text(`${labels.invoiceNumberLabel}: ${invoiceNumber}`, 14, yPos);
  yPos += 7;

  doc.text(`${labels.invoiceDateLabel}: ${formattedDate}`, 14, yPos);
  yPos += 7;

  doc.text(`${labels.dueDateLabel}: ${formattedDueDate}`, 14, yPos);
  yPos += 7;

  if (quoteId) {
    doc.text(`${labels.quoteRefLabel}: ${quoteId.slice(0, 8).toUpperCase()}`, 14, yPos);
    yPos += 7;
  }

  if (structured_data.client_name) {
    doc.text(`${labels.billTo}: ${structured_data.client_name}`, 14, yPos);
    yPos += 7;
  }

  if (structured_data.client_address) {
    doc.setFontSize(10);
    const addressLines = doc.splitTextToSize(structured_data.client_address, pageWidth - 28);
    doc.text(addressLines, 14, yPos);
    yPos += addressLines.length * 5;
    doc.setFontSize(11);
  }

  yPos += 3;
  return yPos;
}

const DEPRECATED_LABEL_KEYS = [
  'notesHeading',
  'invoiceTitle',
  'invoiceNumberLabel',
  'invoiceDateLabel',
  'dueDateLabel',
  'quoteRefLabel',
  'paymentInfoHeading',
] as const;

function mergeDeprecatedLabels(input: GenerateInvoicePdfInput): PdfLabels {
  const merged: Partial<PdfLabels> = { ...input.labels };
  for (const key of DEPRECATED_LABEL_KEYS) {
    if (input[key] && !input.labels?.[key]) {
      merged[key] = input[key];
    }
  }
  return resolveLabels(merged);
}

export function generateInvoiceFilename(
  invoiceNumber: string,
  clientName: string | null,
  invoiceDate: string,
): string {
  const name = clientName?.trim() || 'untitled';
  return `invoice-${invoiceNumber}-${name}-${invoiceDate}.pdf`;
}

export function generateInvoicePdf(input: GenerateInvoicePdfInput): Blob {
  const {
    structured_data,
    contractor,
    vatRate,
    markupPercent,
    quoteId,
    invoiceNumber,
    locale,
    logo,
  } = input;

  const labels = mergeDeprecatedLabels(input);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;

  // Logo (returns vertical offset, 0 when no logo)
  const logoOffset = renderLogo(doc, logo);

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(labels.invoiceTitle, 14, 22 + logoOffset);

  // Company name below title
  if (contractor.companyName) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(contractor.companyName, 14, 32 + logoOffset);
  }

  // Contractor details (right-aligned block, no IBAN — shown in payment info instead)
  renderContractorDetails(doc, contractor, pageWidth, false, labels, logoOffset);

  // Metadata section
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const metadataStartY = (contractor.companyName ? 42 : 35) + logoOffset;
  const yPos = renderInvoiceMetadata(
    doc,
    {
      structured_data,
      contractor,
      invoiceNumber,
      locale,
      quoteId,
      labels,
    },
    metadataStartY,
  );

  // Build footer rows and render items table
  const footRows = buildFooterRows(structured_data.grand_total, vatRate, markupPercent, labels);
  renderItemsTable(doc, structured_data.items, footRows, yPos, vatRate, markupPercent, labels);

  // Payment info section
  if (contractor.iban) {
    const tableEndY = getTableFinalY(doc);
    const pageHeight = doc.internal.pageSize.height;
    let paymentY = tableEndY + 10;
    if (paymentY + 20 > pageHeight - 20) {
      doc.addPage();
      paymentY = 20;
    }
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(labels.paymentInfoHeading, 14, paymentY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`${labels.ibanLabel}: ${contractor.iban}`, 14, paymentY + 6);
  }

  // Notes section
  if (structured_data.notes) {
    renderNotesSection(doc, structured_data.notes, contractor.iban ? 26 : 10, labels.notesHeading);
  }

  // Page footer
  renderPageFooter(doc, labels.generatedBy);

  return doc.output('blob');
}
